import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { getTagUrl } from '../../utils/UrlUtilities';
import {
    BCS_LEVEL,
    CORRECTOPTION,
    QUESTIONDIFFICULTY,
    QUESTION_TYPES,
    QUESTION_TYPE_LABELS,
    TC_BLANK_LABELS,
    TAGLEVELS,
    BOARD_VALUES,
    DIVISION_VALUES,
    UNIT_VALUES,
    isBoardLevel,
    isDivisionLevel,
    isUnitLevel,
    isLanguageLevel,
    isTimerLevel,
    sortBcsTerms,
} from '../../utils/Constants';
import MediaUploadField from '../MediaUploadField';
import { beautifyText } from '../../utils/BasicUtilities';

// Left side of the split-pane editor. Pre-populated with the CSV row; admin
// tweaks fields and the parent's onChange keeps the draft state in sync.
// Field order + controls mirror QuestionAddForm.js so editing a draft feels
// identical to creating a question manually.
const ParsedDraftEditForm = ({ draft, onChange }) => {
    const CallApi = useApiCaller();
    // useApiCaller returns a fresh function each render. Pin the latest via a ref
    // so fetchTagsForLevel doesn't need it in deps — otherwise the effect re-fires
    // every render and hammers /api/tags. Same pattern as IngestionJobList.
    const callApiRef = useRef(CallApi);
    useEffect(() => { callApiRef.current = CallApi; });

    const [availableTags, setAvailableTags] = useState([]);
    const [extraOptionsShown, setExtraOptionsShown] = useState(0);
    // IELTS MATCHING shared-box editor state (seeded from the draft's metadata). The box + image are
    // synced back into draft.metadata by an effect below so the existing PUT payload persists them.
    const [matchRows, setMatchRows] = useState(() => {
        // Seed from the saved box, then pad to 10 rows (A–J) so all ten option slots are
        // visible/editable by default. Empty rows aren't written back to metadata.match_options.
        const box = (draft.metadata && draft.metadata.match_options) || {};
        const rows = Object.keys(box).map(l => ({ letter: l, text: box[l] }));
        const used = new Set(rows.map(r => String(r.letter).toUpperCase()));
        for (const l of 'ABCDEFGHIJ') {
            if (rows.length >= 10) break;
            if (!used.has(l)) { rows.push({ letter: l, text: '' }); used.add(l); }
        }
        return rows.slice(0, 10);
    });
    const [matchImage, setMatchImage] = useState((draft.metadata && draft.metadata.match_image) || '');

    // Functional update so independent writes in the same tick (e.g. ticking a correct box
    // letter while the matching-box effect syncs metadata) don't clobber each other.
    const set = (patch) => onChange(prev => ({ ...prev, ...patch }));

    const fetchTagsForLevel = useCallback(async () => {
        if (!draft.level) {
            setAvailableTags([]);
            return;
        }
        const res = await callApiRef.current({ url: getTagUrl({ level: draft.level }), method: 'GET', token: true });
        if (res.success) setAvailableTags(res.data.tags || []);
        else setAvailableTags([]);
    }, [draft.level]);

    useEffect(() => { fetchTagsForLevel(); }, [fetchTagsForLevel]);

    const tagsOfType = (type) => [
        ...new Set(availableTags.filter(t => t.tag_type === type && t.level === draft.level).map(t => t.name))
    ];

    // Topics for the current level, narrowed to the draft's subject via the tag's subject field
    // (fallback: legacy metadata.subject). Topics with no subject are kept (degrades to level-only).
    const topicOptions = () => {
        const subs = (draft.subject || '').split(',').map(s => s.trim()).filter(Boolean);
        return [...new Set(
            availableTags
                .filter(t => t.tag_type === 'TOPIC' && t.level === draft.level)
                .filter(t => {
                    const s = t.subject || (t.metadata && t.metadata.subject) || '';
                    return !s || subs.length === 0 || subs.includes(s);
                })
                .map(t => t.name)
        )];
    };

    // BCS-only term dropdown options + the year auto-fill mapping. Sorted
    // newest-first (47th, 46th, …) so admins reach recent terms with one click.
    const bcsTermTagObjects = draft.level === BCS_LEVEL
        ? availableTags.filter(t => t.tag_type === 'BCS_TERM' && t.level === BCS_LEVEL)
        : [];
    const bcsTermNames = sortBcsTerms(bcsTermTagObjects.map(t => t.name));

    const handleBcsTermChange = (newTerm) => {
        // When the year field is empty, look up the picked tag's metadata.year
        // and copy it into the draft. Admin's existing year wins if non-empty.
        const tag = bcsTermTagObjects.find(t => t.name === newTerm);
        const yearFromTag = tag?.metadata?.year;
        if (newTerm && yearFromTag && !(draft.year || '').trim()) {
            set({ bcs_term: newTerm, year: yearFromTag });
        } else {
            set({ bcs_term: newTerm });
        }
    };

    const isText = (draft.question_type || 'MCQ') === 'TEXT';
    const isTC = draft.question_type === 'TEXT_COMPLETION';
    const isSE = draft.question_type === 'SENTENCE_EQUIVALENCE';
    const isBuild = draft.question_type === 'BUILD_SENTENCE';
    const isMatching = draft.question_type === 'MATCHING';
    // Options grid shows for MCQ + SE + BUILD (BUILD uses the option fields as its word tiles);
    // TEXT / Text-Completion / MATCHING hide it (MATCHING uses its own shared-box editor).
    const showOptionsGrid = !isText && !isTC && !isMatching;
    // Letters to render: base A–D, plus any of E–H that already have text (so pushed 8-option
    // questions show all their options); SE forces exactly A–F.
    const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    const visibleLetters = isSE
        ? ['A', 'B', 'C', 'D', 'E', 'F']
        : optionLetters.filter((l, i) => i < 4 + extraOptionsShown || (draft[`option_${l.toLowerCase()}`] || '').trim());
    const presentLetters = visibleLetters.filter(l => (draft[`option_${l.toLowerCase()}`] || '').trim());
    // Text-Completion blanks live in metadata.blanks: [{choices:[3], correct:idx}].
    const tcBlanks = (draft.metadata && draft.metadata.blanks) || [];
    const setBlanks = (blanks) => set({ metadata: { ...(draft.metadata || {}), blanks } });
    // Build-a-Sentence: tiles are the option fields; metadata.correct_order lists the answer
    // sequence as 0-based indices into the present options (in letter order).
    const correctOrder = (draft.metadata && draft.metadata.correct_order) || [];
    const setCorrectOrder = (order) => set({ metadata: { ...(draft.metadata || {}), correct_order: order } });
    const noAnswer = !draft.correct_option;
    // correct_option is stored comma-joined (e.g. "A,C"); drive the checkboxes off
    // the parsed set and write it back sorted in A→D order on every toggle.
    const correctOptions = (draft.correct_option || '')
        .split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    const toggleCorrect = (opt) => {
        const next = new Set(correctOptions);
        if (next.has(opt)) next.delete(opt);
        else next.add(opt);
        set({ correct_option: CORRECTOPTION.filter(o => next.has(o)).join(',') });
    };
    // MATCHING: keep draft.metadata.match_options / match_image in sync with the box editor state.
    // Deps are the local editor state only (changed by user edits), so this never loops.
    useEffect(() => {
        if (draft.question_type !== 'MATCHING') return;
        const box = {};
        matchRows.forEach(r => { if ((r.text || '').trim()) box[r.letter] = r.text.trim(); });
        const md = { ...(draft.metadata || {}), match_options: box };
        if (matchImage.trim()) md.match_image = matchImage.trim(); else delete md.match_image;
        set({ metadata: md });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchRows, matchImage, draft.question_type]);
    // MATCHING box letters can be roman numerals / word-bank tokens (i, ii, iv, A–J) — they are
    // case- and value-sensitive, so DON'T uppercase or constrain to A–H like the MCQ set above,
    // otherwise the "correct" ticks never match the box rows (nothing seeds, nothing toggles).
    const matchCorrect = (draft.correct_option || '').split(',').map(s => s.trim()).filter(Boolean);
    // MATCHING correct letter(s) — ordered by the box rows; supports letters beyond A–H (word banks).
    const toggleMatchCorrect = (letter) => {
        const next = new Set(matchCorrect);
        if (next.has(letter)) next.delete(letter);
        else next.add(letter);
        set({ correct_option: matchRows.map(r => r.letter).filter(l => next.has(l)).join(',') });
    };
    // Kept-but-unrecognized tags (server no longer blanks them; this is informational).
    const unrecognizedTags = draft.metadata?.unrecognized_tags || draft.metadata?.blanked_tags || [];
    // A draft is a passage question when it carries a group key or shared passage text.
    const isPassage = !!((draft.group_key || '').trim() || (draft.passage_text || '').trim());

    return (
        <div>
            {noAnswer && !isText && !isTC && !isBuild && !isMatching && (
                <div className="alert alert-warning py-2 mb-3" role="alert">
                    <strong>⚠ No answer in the CSV row.</strong> Tick the correct option(s) before storing.
                </div>
            )}
            {unrecognizedTags.length > 0 && (
                <div className="alert alert-info py-2 mb-3" role="alert">
                    Tag(s) from the CSV not yet in our taxonomy were kept as-is:{' '}
                    <strong>{unrecognizedTags.join(', ')}</strong>
                    {draft.metadata?.source_tags && (
                        <div className="small text-muted mt-1">
                            From CSV:{' '}
                            {Object.entries(draft.metadata.source_tags)
                                .filter(([k, v]) => v && unrecognizedTags.includes(k))
                                .map(([k, v]) => `${k}="${v}"`)
                                .join(', ')}
                        </div>
                    )}
                </div>
            )}

            {draft.metadata?.missing_figure
                && !(draft.question_image_url || draft.explanation_image_url
                    || ['option_a', 'option_b', 'option_c', 'option_d', 'option_e', 'option_f', 'option_g', 'option_h']
                        .some((k) => draft[`${k}_image_url`])) && (
                <div className="alert alert-danger py-2 mb-3" role="alert">
                    <strong>&lt;img needed&gt;</strong> Claude flagged a figure/image this question needs
                    but could not capture as text. Attach the image (Question image below, or an option/
                    explanation image) before approving.
                </div>
            )}

            {isPassage && (
                <div className="alert alert-primary py-2 mb-3" role="alert">
                    <strong>📘 Passage question.</strong>{' '}
                    Group <code>{draft.group_key || '—'}</code>
                    {draft.group_order != null && draft.group_order !== ''
                        ? <> · question <strong>#{draft.group_order}</strong> in the passage</>
                        : null}
                    . On approve, questions sharing this group are linked under one passage.
                </div>
            )}

            <div className="mb-3">
                <label className="form-label">Level <span className="kp-req">*</span></label>
                <select
                    className="form-select"
                    value={draft.level || ''}
                    onChange={e => set({ level: e.target.value, subject: '', topic: '', exam: '', bcs_term: '' })}
                >
                    <option value="">Select</option>
                    {TAGLEVELS.map(item => item.submenu
                        ? <optgroup key={item.value} label={item.label}>{item.submenu.map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}</optgroup>
                        : <option key={item.value} value={item.value}>{item.label}</option>
                    )}
                </select>
            </div>

            {isDivisionLevel(draft.level) && (
                <div className="mb-3">
                    <label className="form-label">Division <span className="kp-req">*</span></label>
                    <select className="form-select" value={draft.division || ''} onChange={e => set({ division: e.target.value })}>
                        <option value="">Select division</option>
                        {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <small className="form-text text-muted">Required for SSC/HSC questions.</small>
                </div>
            )}

            {isUnitLevel(draft.level) && (
                <div className="mb-3">
                    <label className="form-label">Unit <span className="kp-req">*</span></label>
                    <select className="form-select" value={draft.unit || ''} onChange={e => set({ unit: e.target.value })}>
                        <option value="">Select unit</option>
                        {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <small className="form-text text-muted">Required for VARSITY questions.</small>
                </div>
            )}

            <div className="mb-3">
                <label className="form-label me-3">Question Type</label>
                {QUESTION_TYPES.map(qt => (
                    <div className="form-check form-check-inline" key={qt}>
                        <input
                            className="form-check-input"
                            type="radio"
                            name="draft_question_type"
                            id={`draft-qtype-${qt}`}
                            checked={(draft.question_type || 'MCQ') === qt}
                            onChange={() => set({ question_type: qt })}
                        />
                        <label className="form-check-label" htmlFor={`draft-qtype-${qt}`}>
                            {QUESTION_TYPE_LABELS[qt] || qt}
                        </label>
                    </div>
                ))}
            </div>

            {isLanguageLevel(draft.level) && (
                <div className="mb-3">
                    <label className="form-label">Side text / passage (optional)</label>
                    <textarea className="form-control" rows={2} value={draft.side_text || ''} onChange={e => set({ side_text: e.target.value })} />
                    <MediaUploadField label="Audio (optional)" value={draft.audio_url || ''} onChange={(url) => set({ audio_url: url })} kind="audio" />
                </div>
            )}

            {/* Passage editing — shown for any grouped/passage draft regardless of level.
              * The shared passage, its group key, and this question's order in the passage
              * drive the QuestionGroup rebuilt on approve. */}
            {isPassage && (
                <div className="mb-3 p-2 border border-primary-subtle rounded" style={{ background: '#faf5ff' }}>
                    <label className="form-label mb-1">Passage text (shared across the group)</label>
                    <textarea className="form-control" rows={4} value={draft.passage_text || ''} onChange={e => set({ passage_text: e.target.value })} />
                    <div className="row g-2 mt-1">
                        <div className="col-md-6">
                            <label className="form-label mb-1">Passage group key</label>
                            <input type="text" className="form-control" value={draft.group_key || ''} onChange={e => set({ group_key: e.target.value })} />
                            <small className="form-text text-muted">Questions sharing this key form one passage.</small>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label mb-1">Order in passage</label>
                            <input type="number" min="0" className="form-control" value={draft.group_order ?? ''} onChange={e => set({ group_order: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                    </div>
                    <MediaUploadField label="Passage audio (optional)" value={draft.audio_url || ''} onChange={(url) => set({ audio_url: url })} kind="audio" />
                </div>
            )}

            <div className="mb-3">
                <label className="form-label">Question Text <span className="kp-req">*</span></label>
                <MediaUploadField label="Question image (optional)" value={draft.question_image_url || ''} onChange={(url) => set({ question_image_url: url })} kind="image" />
                <textarea
                    className="form-control"
                    rows={3}
                    value={draft.question_text || ''}
                    onChange={e => set({ question_text: e.target.value })}
                />
            </div>

            {isText && (
                <div className="mb-3">
                    <label className="form-label">Text Answer (reference)</label>
                    <small className="form-text text-muted d-block">Set a reference to auto-grade (case/space-insensitive). Leave blank to grade manually. Separate multiple acceptable answers with <code>/</code> (spaces around each are ignored), e.g. <code>2575 / 2,575</code>. Answer-key notation is also accepted: <code>10/ten</code>, <code>grass(es)</code>, <code>(land) surface</code>.</small>
                    <textarea className="form-control" rows={2} value={draft.text_answer || ''} onChange={e => set({ text_answer: e.target.value })} />
                </div>
            )}

            {/* IELTS MATCHING: the shared option box (A, B, C…) every item chooses from + optional
              * map/plan/diagram image. Tick the letter(s) correct for THIS item. */}
            {isMatching && (
                <div className="mb-3 border rounded p-2">
                    <label className="form-label mb-1 fw-bold">Matching option box (shared)</label>
                    <small className="form-text text-muted d-block mb-2">The list of options every item in this set chooses from. Tick the one(s) correct for THIS item.</small>
                    {matchRows.map((row, i) => (
                        <div key={i} className="d-flex align-items-center gap-2 mb-1">
                            <span className="fw-bold" style={{ width: '1.5rem' }}>{row.letter}</span>
                            <input className="form-control form-control-sm" value={row.text}
                                onChange={e => setMatchRows(rows => rows.map((r, j) => j === i ? { ...r, text: e.target.value } : r))}
                                placeholder={`Option ${row.letter} text`} />
                            <div className="form-check mb-0" title="Correct for this item">
                                <input type="checkbox" className="form-check-input" checked={matchCorrect.includes(row.letter)}
                                    onChange={() => toggleMatchCorrect(row.letter)} />
                                <label className="form-check-label small">correct</label>
                            </div>
                            {matchRows.length > 2 && (
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setMatchRows(rows => rows.filter((_, j) => j !== i))}>×</button>
                            )}
                        </div>
                    ))}
                    {matchRows.length < 10 && (
                        <button type="button" className="btn btn-sm btn-outline-secondary mt-1"
                            onClick={() => setMatchRows(rows => [...rows, { letter: String.fromCharCode(65 + rows.length), text: '' }])}>
                            + Add option
                        </button>
                    )}
                    <div className="mt-2">
                        <MediaUploadField label="Shared map / plan / diagram image (optional)" value={matchImage} onChange={setMatchImage} kind="image" />
                    </div>
                </div>
            )}

            {showOptionsGrid && visibleLetters.map(opt => {
                const field = `option_${opt.toLowerCase()}`;
                const imgField = `${field}_image_url`;
                return (
                    <div className="mb-3" key={opt}>
                        <label className="form-label">Option {opt}{opt <= 'D' ? <span className="kp-req"> *</span> : <span className="text-muted"> (optional)</span>}</label>
                        <MediaUploadField label={`Option ${opt} image (optional)`} value={draft[imgField] || ''} onChange={(url) => set({ [imgField]: url })} kind="image" />
                        <input
                            type="text"
                            className="form-control"
                            value={draft[field] || ''}
                            onChange={e => set({ [field]: e.target.value })}
                        />
                    </div>
                );
            })}

            {showOptionsGrid && !isSE && (4 + extraOptionsShown) < 8 && (
                <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setExtraOptionsShown(n => Math.min(4, n + 2))}>
                    + Add two more options
                </button>
            )}
            {isSE && <div className="form-text text-muted mb-2">Sentence Equivalence: 6 options; tick exactly the 2 correct answers.</div>}

            {showOptionsGrid && !isBuild && (
                <div className="mb-3">
                    <label className="form-label mb-1 d-block">Correct Option(s) <span className="kp-req">*</span></label>
                    <div className="d-flex flex-wrap gap-3">
                        {presentLetters.map(opt => (
                            <div className="form-check" key={opt}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`draft-correct-${opt}`}
                                    checked={correctOptions.includes(opt)}
                                    onChange={() => toggleCorrect(opt)}
                                />
                                <label className="form-check-label" htmlFor={`draft-correct-${opt}`}>{opt}</label>
                            </div>
                        ))}
                    </div>
                    <small className="form-text text-muted">
                        {isSE ? 'Tick exactly two correct options.' : 'Tick every correct option — supports “select all that apply”. Only filled options are shown.'}
                    </small>
                </div>
            )}

            {/* TOEFL Build a Sentence: tiles are the option fields; click them in order. */}
            {isBuild && (
                <div className="mb-3">
                    <label className="form-label mb-1 d-block">Correct word order <span className="kp-req">*</span></label>
                    <div className="form-text text-muted mb-2">Fill the option fields above with the (shuffled) word tiles, then click them in the correct sentence order. Leftover tiles act as distractors — you don't have to place every tile.</div>
                    <div className="border rounded p-2 mb-2 d-flex flex-wrap gap-2" style={{ minHeight: '2.2rem' }}>
                        {correctOrder.length === 0 && <span className="text-muted">Click tiles below to set the order…</span>}
                        {correctOrder.filter(i => i < presentLetters.length).map((i, pos) => (
                            <button type="button" key={pos} className="btn btn-sm btn-primary" onClick={() => setCorrectOrder(correctOrder.filter(x => x !== i))} title="Click to remove">
                                {pos + 1}. {draft[`option_${presentLetters[i].toLowerCase()}`]}
                            </button>
                        ))}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {presentLetters.map((opt, i) => (
                            <button type="button" key={opt} disabled={correctOrder.includes(i)} className="btn btn-sm btn-outline-secondary"
                                onClick={() => setCorrectOrder([...correctOrder, i])}>
                                {draft[`option_${opt.toLowerCase()}`] || opt}
                            </button>
                        ))}
                    </div>
                    {correctOrder.length > 0 && (
                        <button type="button" className="btn btn-sm btn-link text-danger p-0 mt-1" onClick={() => setCorrectOrder([])}>Reset order</button>
                    )}
                </div>
            )}

            {/* GRE Text Completion: 2–3 blanks, each with 3 choices; mark the correct one. */}
            {isTC && (
                <div className="mb-3">
                    <label className="form-label mb-1 d-block">Blanks <span className="kp-req">*</span></label>
                    {tcBlanks.map((blank, bi) => (
                        <div className="border rounded p-2 mb-2" key={bi}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold">Blank {TC_BLANK_LABELS[bi] || `#${bi + 1}`}</span>
                                {tcBlanks.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        title={`Remove Blank ${TC_BLANK_LABELS[bi] || `#${bi + 1}`}`}
                                        onClick={() => setBlanks(tcBlanks.filter((_, i) => i !== bi))}
                                    >×</button>
                                )}
                            </div>
                            {[0, 1, 2].map(ci => (
                                <div className="d-flex align-items-center gap-2 mb-1" key={ci}>
                                    <input
                                        type="radio"
                                        name={`draft-blank-${bi}`}
                                        checked={blank.correct === ci}
                                        onChange={() => setBlanks(tcBlanks.map((b, i) => i === bi ? { ...b, correct: ci } : b))}
                                        title="Mark correct"
                                    />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder={`Choice ${ci + 1}`}
                                        value={(blank.choices && blank.choices[ci]) || ''}
                                        onChange={e => setBlanks(tcBlanks.map((b, i) => i === bi ? { ...b, choices: [0, 1, 2].map(j => j === ci ? e.target.value : ((b.choices && b.choices[j]) || '')) } : b))}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
                    <div className="d-flex gap-2">
                        {tcBlanks.length < 3 && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setBlanks([...tcBlanks, { choices: ['', '', ''], correct: 0 }])}>+ Add blank</button>
                        )}
                    </div>
                </div>
            )}

            <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label mb-0">Explanation</label>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title="Collapse line breaks into one clean paragraph"
                        onClick={() => set({ answer_explanation: beautifyText(draft.answer_explanation || '') })}
                    >Beautify</button>
                </div>
                <MediaUploadField label="Explanation image (optional)" value={draft.explanation_image_url || ''} onChange={(url) => set({ explanation_image_url: url })} kind="image" />
                <textarea
                    className="form-control"
                    rows={2}
                    value={draft.answer_explanation || ''}
                    onChange={e => set({ answer_explanation: e.target.value })}
                />
            </div>

            <div className="mb-3">
                <label className="form-label">Difficulty <span className="kp-req">*</span></label>
                <select
                    className="form-select"
                    value={draft.difficulty || 'MEDIUM'}
                    onChange={e => set({ difficulty: e.target.value })}
                >
                    {QUESTIONDIFFICULTY.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>

            <div className="mb-3">
                <label className="form-label">Subject <span className="kp-req">*</span></label>
                <select
                    className="form-select"
                    value={draft.subject || ''}
                    onChange={e => set({ subject: e.target.value })}
                    disabled={!draft.level || tagsOfType('SUBJECT').length === 0}
                >
                    <option value="">Select</option>
                    {tagsOfType('SUBJECT').map(t => <option key={t} value={t}>{t}</option>)}
                    {draft.subject && !tagsOfType('SUBJECT').includes(draft.subject)
                        && <option value={draft.subject}>{draft.subject} (custom)</option>}
                </select>
                {!draft.level && <small className="form-text text-muted">Select a Level to add a Subject.</small>}
            </div>

            <div className="mb-3">
                <label className="form-label">Topic</label>
                <select
                    className="form-select"
                    value={draft.topic || ''}
                    onChange={e => set({ topic: e.target.value })}
                    disabled={!draft.level || topicOptions().length === 0}
                >
                    <option value="">Select</option>
                    {topicOptions().map(t => <option key={t} value={t}>{t}</option>)}
                    {draft.topic && !topicOptions().includes(draft.topic)
                        && <option value={draft.topic}>{draft.topic} (custom)</option>}
                </select>
            </div>

            {!isLanguageLevel(draft.level) && (
                <div className="mb-3">
                    <label className="form-label">Exam</label>
                    <select
                        className="form-select"
                        value={draft.exam || ''}
                        onChange={e => set({ exam: e.target.value })}
                        disabled={!draft.level || tagsOfType('EXAM').length === 0}
                    >
                        <option value="">Select</option>
                        {tagsOfType('EXAM').map(t => <option key={t} value={t}>{t}</option>)}
                        {draft.exam && !tagsOfType('EXAM').includes(draft.exam)
                            && <option value={draft.exam}>{draft.exam} (custom)</option>}
                    </select>
                </div>
            )}

            {isBoardLevel(draft.level) && (
                <div className="mb-3">
                    <label className="form-label">Board</label>
                    <select className="form-select" value={draft.board || ''} onChange={e => set({ board: e.target.value })}>
                        <option value="">Select board</option>
                        {BOARD_VALUES.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                </div>
            )}

            {isTimerLevel(draft.level) && (
                <div className="mb-3">
                    <label className="form-label">Solving time (seconds, optional)</label>
                    <input type="number" min="0" className="form-control" value={draft.solving_time_seconds ?? ''} onChange={e => set({ solving_time_seconds: e.target.value === '' ? null : Number(e.target.value) })} />
                </div>
            )}

            {/* BCS Term — only when this draft is level=BCS. Picking a term
              * auto-fills the year from the tag's metadata.year (if year empty). */}
            {draft.level === BCS_LEVEL && (
                <div className="mb-3">
                    <label className="form-label">BCS Term</label>
                    <select
                        className="form-select"
                        value={draft.bcs_term || ''}
                        onChange={e => handleBcsTermChange(e.target.value)}
                        disabled={bcsTermNames.length === 0}
                    >
                        <option value="">Select</option>
                        {bcsTermNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                        {draft.bcs_term && !bcsTermNames.includes(draft.bcs_term)
                            && <option value={draft.bcs_term}>{draft.bcs_term} (custom)</option>}
                    </select>
                    <small className="form-text text-muted">
                        Picking a term auto-fills the year (if empty).
                    </small>
                </div>
            )}

            <div className="mb-3">
                <label className="form-label">Exam Year</label>
                <input
                    type="text"
                    className="form-control"
                    placeholder="2024 or 2020,2021"
                    value={draft.year || ''}
                    onChange={e => set({ year: e.target.value })}
                />
            </div>
        </div>
    );
};

export default ParsedDraftEditForm;
